import React, { useEffect } from "react";
import {
    Paper,
    Skeleton,
    Typography,
    Box,
    Alert,
    Divider,
} from "@mui/material";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useCustomPageController } from "./context/custom-page-controller";
import { useCustomPageState } from "./context/custom-page-context";
import PageHeader from "../components/shared/page-header";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { RichTextContent } from "./rich-text-content";
import { CustomPageSections } from "./custom-page-sections";

export function CustomPageComponent({ legacyArticle = false }: { legacyArticle?: boolean }) {
    const { loadArticle, loadCustomPage } = useCustomPageController();
    const { article, page, loading, error } = useCustomPageState();
    // Slug da página customizada ("/pagina/{slug}") ou, na rota antiga, o id do artigo legado
    const { slug } = useParams();

    useEffect(() => {
        if (!slug) return;

        if (legacyArticle) {
            loadArticle(Number(slug));
        } else {
            loadCustomPage(slug);
        }
    }, [slug, legacyArticle]);

    useEffect(() => {
        if (page?.title) {
            document.title = page.title;
        }
    }, [page?.title]);

    if (loading) {
        return (
            <Box>
                <PageHeader
                    title="Carregando..."
                    subtitle=""
                    breadcrumbs={[
                        { label: 'Carregando...' }
                    ]}
                />
                <Box>
                    <Skeleton variant="text" width="60%" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="30%" height={40} sx={{ mb: 3 }} />
                    <Skeleton variant="rounded" width="100%" height={400} sx={{ borderRadius: 2, mb: 3 }} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="80%" height={30} />
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <PageHeader
                    title="Erro"
                    subtitle=""
                    breadcrumbs={[
                        { label: 'Erro' }
                    ]}
                />
                <Alert severity="error" sx={{ mt: 3 }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    if (page) {
        const lastUpdate = page.updatedAt || page.publishedAt;

        return (
            <Box>
                <PageHeader
                    title={page.title || ''}
                    subtitle={page.summary || ''}
                    breadcrumbs={[
                        { label: page.title || '' }
                    ]}
                />

                {page.coverImageUrl && (
                    <Box
                        component="img"
                        src={page.coverImageUrl}
                        alt={page.title || ''}
                        sx={{ width: '100%', maxHeight: 420, objectFit: 'cover', borderRadius: 2, display: 'block', mb: 3 }}
                    />
                )}

                <CustomPageSections sections={page.sections} />

                {lastUpdate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 3 }}>
                        <CalendarTodayIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                            Atualizado em: {dayjs(lastUpdate).format('DD/MM/YYYY')}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    }

    if (!article) {
        return (
            <Box>
                <PageHeader
                    title="Página não encontrada"
                    subtitle=""
                    breadcrumbs={[
                        { label: 'Não encontrada' }
                    ]}
                />
                <Alert severity="warning" sx={{ mt: 3 }}>
                    A página solicitada não foi encontrada ou não está mais disponível.
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            <PageHeader
                title={article.title || ''}
                subtitle=""
                breadcrumbs={[
                    { label: article.title || '' }
                ]}
            />

            <Paper sx={{ p: 4 }} variant="outlined">
                {/* Cabeçalho com metadados */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Criado em: {article.createdDate
                                    ? dayjs(article.createdDate).format('DD/MM/YYYY')
                                    : 'N/A'}
                            </Typography>
                        </Box>

                        {article.modifiedDate && (
                            <>
                                <Divider orientation="vertical" flexItem />
                                <Typography variant="body2" color="text.secondary">
                                    Atualizado em: {dayjs(article.modifiedDate).format('DD/MM/YYYY')}
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Conteúdo do artigo */}
                <RichTextContent html={article.content} />
            </Paper>
        </Box>
    );
}
